/**
 * How It Works — the interactive ALIS experience.
 *
 * Nested router (SchedulePage precedent) owning the hub landing page and
 * the three chapters, each a TourShell with its own slide layout:
 *
 *   /how-it-works                  → hub (three chapter cards)
 *   /how-it-works/story            → The Story (illustrated narrative)
 *   /how-it-works/workflow         → The Workflow (connected-flow infographics)
 *   /how-it-works/around-the-site  → Around the Site (annotated screenshots)
 *
 * The audio engine is instantiated ONCE here and passed down, so the music
 * loop keeps playing across hub ↔ chapter navigation and only stops when
 * the user leaves /how-it-works/* entirely.
 */
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { HowItWorksHubPage } from './HowItWorksHubPage';
import { SiteSlide } from './SiteSlide';
import { TourShell } from './TourShell';
import { TourSlide } from './TourSlide';
import { WorkflowSlide } from './WorkflowSlide';
import { SITE_SLIDES } from './siteSlides';
import { TOUR_SLIDES } from './tourSlides';
import { WORKFLOW_SLIDES } from './workflowSlides';
import { useTourAudio, type TourAudio } from './useTourAudio';

const StoryChapter = ({ audio }: { audio: TourAudio }) => {
  const navigate = useNavigate();
  return (
    <TourShell
      slides={TOUR_SLIDES}
      chapterLabel="Chapter 1 · The Story"
      renderSlide={(slide, direction) => <TourSlide slide={slide} direction={direction} />}
      onExit={() => navigate(ROUTES.HOW_IT_WORKS)}
      audio={audio}
      finale={{
        confetti: true,
        ctaLabel: 'Enter ALIS',
        onCta: () => {
          audio.fadeOutMusic();
          navigate(ROUTES.DASHBOARD);
        },
      }}
    />
  );
};

const WorkflowChapter = ({ audio }: { audio: TourAudio }) => {
  const navigate = useNavigate();
  return (
    <TourShell
      slides={WORKFLOW_SLIDES}
      chapterLabel="Chapter 2 · The Workflow"
      renderSlide={(slide, direction) => <WorkflowSlide slide={slide} direction={direction} />}
      onExit={() => navigate(ROUTES.HOW_IT_WORKS)}
      audio={audio}
      finale={{
        ctaLabel: 'Next chapter: Around the Site',
        onCta: () => navigate(ROUTES.HOW_IT_WORKS_SITE_TOUR),
      }}
    />
  );
};

const SiteChapter = ({ audio }: { audio: TourAudio }) => {
  const navigate = useNavigate();
  return (
    <TourShell
      slides={SITE_SLIDES}
      chapterLabel="Chapter 3 · Around the Site"
      renderSlide={(slide, direction) => <SiteSlide slide={slide} direction={direction} />}
      onExit={() => navigate(ROUTES.HOW_IT_WORKS)}
      audio={audio}
      finale={{
        ctaLabel: 'Enter ALIS',
        onCta: () => {
          audio.fadeOutMusic();
          navigate(ROUTES.DASHBOARD);
        },
      }}
    />
  );
};

export const HowItWorksPage = () => {
  const audio = useTourAudio();
  return (
    <Routes>
      <Route index element={<HowItWorksHubPage audio={audio} />} />
      <Route path="story" element={<StoryChapter audio={audio} />} />
      <Route path="workflow" element={<WorkflowChapter audio={audio} />} />
      <Route path="around-the-site" element={<SiteChapter audio={audio} />} />
      <Route path="*" element={<Navigate to={ROUTES.HOW_IT_WORKS} replace />} />
    </Routes>
  );
};
